/**
 * Memory Graph: shared DTOs for the durable Career Memory store + the graph
 * projection (M5.1). Mirrors the Prisma models in
 * `packages/db/prisma/schema/memory.prisma`.
 *
 * The graph is a VIEW over existing rows plus the durable fact/preference store;
 * nodes reference source rows by (refTable, refId) and never duplicate them.
 *
 * Runtime const arrays (GRAPH_NODE_TYPES, GRAPH_EDGE_TYPES) are re-exported
 * through `memory-graph.runtime.js` so nodenext ESM consumers resolve them -
 * import the VALUES from `@tabbio/types` (which points at `.runtime.js`) and the
 * TYPES from here. Reuses APIResponse<T> from `./api`.
 */

// ── Node types (LOCKED catalog; const array backs the union) ──────────────────
export const GRAPH_NODE_TYPES = [
  'cv',
  'section',
  'skill',
  'experience',
  'education',
  'certification',
  'job',
  'application',
  'company',
  'recruiter',
  'connector',
  'agent',
  'automation',
  'episode',
  'fact',
  'preference',
  'knowledge_asset',
] as const;
export type GraphNodeType = (typeof GRAPH_NODE_TYPES)[number];

// ── Edge types (LOCKED catalog; const array backs the union) ──────────────────
export const GRAPH_EDGE_TYPES = [
  'CV_HAS_SECTION',
  'CV_HAS_SKILL',
  'SECTION_USES_SKILL',
  'USER_HAS_EXPERIENCE',
  'USER_HAS_EDUCATION',
  'USER_HAS_CERTIFICATION',
  'USER_APPLIED_JOB',
  'USER_SAVED_JOB',
  'JOB_MATCHED_USER',
  'JOB_REQUIRES_SKILL',
  'JOB_AT_COMPANY',
  'RECRUITER_WORKS_AT_COMPANY',
  'AGENT_HAS_ACCESS',
  'AGENT_GENERATED_EPISODE',
  'AUTOMATION_READS_NODE',
  'AUTOMATION_WRITES_NODE',
  'CONNECTOR_FEEDS_NODE',
  'FACT_DERIVED_FROM_EPISODE',
  'PREFERENCE_OF_USER',
  'EPISODE_ABOUT_NODE',
] as const;
export type GraphEdgeType = (typeof GRAPH_EDGE_TYPES)[number];

// ── Graph projection DTOs ─────────────────────────────────────────────────────

export interface GraphNode {
  id: string;
  nodeType: GraphNodeType;
  refTable: string;
  refId: string;
  label: string;
  sublabel?: string | null;
  properties: Record<string, unknown>;
  /** Server-computed layout. */
  x?: number | null;
  y?: number | null;
  ring?: number | null;
  clusterKey?: string | null;
  companyId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GraphEdge {
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
  edgeType: GraphEdgeType;
  weight?: number | null;
  properties: Record<string, unknown>;
  createdAt: string;
}

/** Server-computed layout keyed by node id. */
export type MemoryGraphLayout = Record<
  string,
  { x: number; y: number; ring: number }
>;

export interface MemoryGraphMeta {
  nodeCount: number;
  edgeCount: number;
  /** Plan node cap (null = unlimited). */
  cap: number | null;
  /** Plan visible-window in days. */
  windowDays: number;
  /** True when the total node count exceeded the plan cap. */
  truncated: boolean;
  /** True when the Pro extended-memory window/cap is active. */
  extendedMemory: boolean;
}

export interface MemoryGraphPayload {
  nodes: GraphNode[];
  edges: GraphEdge[];
  layout: MemoryGraphLayout;
  meta: MemoryGraphMeta;
}

export interface MemoryGraphQuery {
  /** Optional node-type filter. */
  types?: GraphNodeType[];
  /** ISO date: only nodes created on/after this. */
  since?: string;
}

// ── Durable store DTOs ────────────────────────────────────────────────────────

export interface Episode {
  id: string;
  kind: string;
  content: string;
  sourceType: string;
  sourceRefId?: string | null;
  agentKey?: string | null;
  graphNodeId?: string | null;
  companyId?: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  deletedAt?: string | null;
}

export interface SemanticFact {
  id: string;
  kind: string;
  summary: string;
  skillId?: string | null;
  jobId?: string | null;
  confidence?: number | null;
  companyId?: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  deletedAt?: string | null;
}

export interface Preference {
  id: string;
  preferenceType: string;
  value: Record<string, unknown>;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

export interface MemoryAccessGrant {
  id: string;
  agentKey: string;
  canRead: boolean;
  canWrite: boolean;
  grantedAt: string;
  revokedAt?: string | null;
}

// ── Endpoint payloads ─────────────────────────────────────────────────────────

export interface MemoryRecallRequest {
  query: string;
  k?: number;
}

export interface MemoryRecallHit {
  node: GraphNode;
  distance: number;
}

export interface MemoryRecallResult {
  /** Nearest nodes by cosine distance (ivfflat). */
  hits: MemoryRecallHit[];
  /** 1–2 hop neighbour nodes of the hits. */
  relatedNodes: GraphNode[];
  /** Edges connecting hits ↔ related nodes. */
  edges: GraphEdge[];
}

export interface MemoryEpisodeCreateRequest {
  content: string;
  kind?: string;
  sourceType?: string;
  sourceRefId?: string;
  agentKey?: string;
}

export interface MemoryExport {
  episodes: Episode[];
  facts: SemanticFact[];
  preferences: Preference[];
  grants: MemoryAccessGrant[];
  exportedAt: string;
}
